import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import "../../Assets/scss/components/trustedipaddress.scss";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { SET_MESSAGE } from "../../Actions/Types";
import {
  getUserIPAddress,
  saveUserIP,
  deleteUserIP,
} from "../../Api/IPaddressAPI/index";

// Props for TrustedIPManager component
interface TrustedIPManagerProps {
  userId: string;
  handleToggleIPRestriction: (checked: boolean) => Promise<void>;
  onBack: () => void; // ← NEW
}

// Type for IP records
type IPRecord = {
  id: number;
  ip: string;
};

const TrustedIPManager: React.FC<TrustedIPManagerProps> = ({
  userId,
  handleToggleIPRestriction,
  onBack,
}) => {
  const { accessToken } = useSelector((store: StoreProptypes) => store);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // State to store all trusted IPs
  const [ipList, setIPList] = useState<IPRecord[]>([]);

  // State to handle new IP input
  const [newIP, setNewIP] = useState("");

  /**
   * Utility: Validate IPv4 format
   */
  const isValidIP = (ip: string): boolean => {
    const ipRegex =
      /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
    return ipRegex.test(ip.trim());
  };

  /**
   * Fetch all trusted IPs from server
   */
  const fetchUserIPAddress = (): void => {
    if (accessToken) {
      getUserIPAddress(accessToken)
        .then((res) => {
          setIPList(res?.data || []);
        })
        .catch((err) => {
          dispatch({
            type: SET_MESSAGE,
            payload: {
              toast_message: `${err}`,
              toast_type: "danger",
            },
          });
        });
    }
  };

  // Initial fetch of IP addresses
  useEffect(() => {
    fetchUserIPAddress();
  }, [accessToken]);

  /**
   * Save new IP address
   * If it's the first IP, also enable IP restriction
   */
  const handleSaveIP = async (ip: string) => {
    if (!accessToken) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Access token is missing."),
          toast_type: "danger",
        },
      });
      return;
    }

    if (!ip) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("IP address is required."),
          toast_type: "danger",
        },
      });
      return;
    }

    if (!isValidIP(ip)) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Invalid IP address format."),
          toast_type: "danger",
        },
      });
      return;
    }

    try {
      const isFirstIP = ipList.length === 0; // store previous state before save

      const response = await saveUserIP(accessToken, { ip });
      if (response?.message) {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: response.message || t("IP saved successfully."),
            toast_type: "success",
          },
        });

        setNewIP(""); // clear input field
        fetchUserIPAddress(); // refresh IP list

        // If first IP is added, enable restriction
        if (isFirstIP) {
          await handleToggleIPRestriction(true);
        }
      }
    } catch (error) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Error saving IP. Please try again."),
          toast_type: "danger",
        },
      });
    }
  };

  /**
   * Delete selected IP address
   * If it's the last remaining IP, also disable IP restriction
   */
  const handleDeleteIP = async (id: number) => {
    if (!accessToken) return;

    try {
      const isLastIP = ipList.length === 1; // store previous state before delete

      const res = await deleteUserIP(accessToken, id);
      if (res?.message) {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: t("IP deleted successfully."),
            toast_type: "success",
          },
        });

        setIPList((prev) => prev.filter((item) => item.id !== id)); // update list

        // If last IP deleted, disable restriction
        if (isLastIP) {
          await handleToggleIPRestriction(false);
        }
      }
    } catch (err) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Error deleting IP."),
          toast_type: "danger",
        },
      });
    }
  };

  /**
   * Render Trusted IP Manager UI
   */
  return (
    <div className="trusted-ip-manager">
      <div className="trusted-ip-header d-flex justify-content-between align-items-center mb-4 flex-wrap">
        <h4 className="mb-0 h5">{t("IP Addresses")}</h4>
        <Button variant="primary" className="back-icon-btn" onClick={onBack}>
          <i className="material-symbols-outlined" aria-hidden="true">
            arrow_left_alt
          </i>
        </Button>
      </div>

      {/* Input row to add new IP */}
      <div className="ip-input-row">
        <Form.Control
          placeholder={t("Enter IP address")}
          value={newIP}
          onChange={(e) => setNewIP(e.target.value)}
        />
        <Button
          variant="primary"
          className="add-btn"
          onClick={() => handleSaveIP(newIP)}
        >
          {t("Add")}
        </Button>
      </div>

      {/* List of saved IPs */}
      <div className="ip-list">
        {ipList?.map((i) => (
          <div key={i.id} className="ip-item">
            <div className="ip-info">
              <i className="material-symbols-outlined" aria-hidden="true">
                {t("security")}
              </i>
              <span>{i.ip}</span>
            </div>
            <Button
              size="sm"
              variant="danger"
              className="delete-btn"
              onClick={() => handleDeleteIP(i.id)}
            >
              {t("Delete")}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrustedIPManager;
